import React, { useState, useEffect } from 'react';
import { tacticalApi } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { isFlywheelEra } from './tactical-editor/FlywheelSection';
import { isRiscEra } from './tactical-editor/RiscSection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { Toaster, toast } from 'sonner';
import { 
  ArrowLeft, 
  Archive,
  Calendar,
  FileText
} from 'lucide-react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_COLORS = {
  green: 'bg-emerald-100 text-emerald-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  orange: 'bg-orange-100 text-orange-700',
  red: 'bg-red-100 text-red-700',
};

const ArchiveView = ({ onBack }) => {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArchives();
  }, []);

  const loadArchives = async () => {
    setLoading(true);
    try {
      const data = await tacticalApi.getArchives();
      setArchives(data);
    } catch (err) {
      console.error('Failed to load archives:', err);
      toast.error('Failed to load archives');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8" data-testid="archives-loading">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-10 w-48 mb-8" />
          <Skeleton className="h-32 mb-4" />
          <Skeleton className="h-32 mb-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="archive-view">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-lg font-semibold">Tactical Archives</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 flex items-center gap-3">
            <Archive className="w-6 h-6 text-muted-foreground" />
            Past Tacticals
          </h2>
          <p className="text-muted-foreground">
            Review your archived monthly tacticals.
          </p>
        </div>

        {archives.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Archives Yet</h3>
              <p className="text-muted-foreground">
                Your archived tacticals will appear here after monthly rollovers.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Accordion type="single" collapsible className="space-y-4">
            {archives.map((tactical) => (
              <AccordionItem 
                key={tactical.id} 
                value={tactical.id}
                className="border border-slate-200 rounded-lg bg-white overflow-hidden"
                data-testid={`archive-${tactical.id}`}
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <Calendar className="w-5 h-5 text-primary" />
                    <div className="text-left">
                      <h3 className="font-semibold">
                        {MONTHS[tactical.month - 1]} {tactical.year}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Archived on {new Date(tactical.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  {/* Development Section */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-sm text-muted-foreground mb-2">
                      Personal & Professional Development
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Personal:</span>{' '}
                        {tactical.development?.personal || 'Not specified'}
                      </div>
                      <div>
                        <span className="font-medium">Professional:</span>{' '}
                        {tactical.development?.professional || 'Not specified'}
                      </div>
                      <div>
                        <span className="font-medium">Soft Skills:</span>{' '}
                        {tactical.development?.soft_skills || 'Not specified'}
                      </div>
                    </div>
                  </div>

                  {/* Scorecard Section — replaced by Flywheel image starting Sept 2026 */}
                  {isFlywheelEra(tactical.month, tactical.year) ? (
                    <div className="mb-6" data-testid="archive-flywheel">
                      <h4 className="font-semibold text-sm text-muted-foreground mb-2">Flywheel</h4>
                      <div className="flex items-center justify-center py-2">
                        <img
                          src="/images/flywheel.png"
                          alt="TMC Growth Flywheel"
                          className="max-w-full h-auto max-h-[260px] object-contain"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <h4 className="font-semibold text-sm text-muted-foreground mb-2">
                        Scorecard
                      </h4>
                      <div className="space-y-1">
                        {tactical.scorecard?.map((item, index) => (
                          <div key={item.name || index} className="flex items-center justify-between text-sm py-1">
                            <span className="truncate pr-4">{item.name}</span>
                            <Badge className={STATUS_COLORS[item.status] || STATUS_COLORS.green}>
                              {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lightning Round Section */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-sm text-muted-foreground mb-2">
                      Lightning Round
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Priority 1:</span>{' '}
                        {tactical.lightning_round?.priority1 || 'Not specified'}
                      </div>
                      <div>
                        <span className="font-medium">Priority 2:</span>{' '}
                        {tactical.lightning_round?.priority2 || 'Not specified'}
                      </div>
                      <div>
                        <span className="font-medium">Priority 3:</span>{' '}
                        {tactical.lightning_round?.priority3 || 'Not specified'}
                      </div>
                    </div>
                  </div>

                  {/* R.I.S.C Section — October 2026+ tacticals only */}
                  {isRiscEra(tactical.month, tactical.year) && tactical.risc && (
                    <div className="mb-6" data-testid="archive-risc">
                      <h4 className="font-semibold text-sm text-muted-foreground mb-2">R.I.S.C.</h4>
                      <div className="space-y-2 text-sm">
                        {[
                          { key: 'revolving_door', label: 'R · Revolving Door' },
                          { key: 'indifference',   label: 'I · Indifference' },
                          { key: 'silence',        label: 'S · Silence' },
                          { key: 'communications', label: 'C · Communications' },
                        ].map(({ key, label }) => {
                          const item = tactical.risc?.[key] || {};
                          const isEmpty = !item.who && !item.what && !item.action_taken;
                          return (
                            <div key={key} className="border border-slate-100 rounded-md p-2">
                              <div className="font-medium text-slate-700 mb-1">{label}</div>
                              {isEmpty ? (
                                <div className="text-slate-400 italic text-xs">No entry</div>
                              ) : (
                                <div className="text-xs space-y-0.5">
                                  {item.who && <div><span className="font-semibold">Who:</span> {item.who}</div>}
                                  {item.what && <div><span className="font-semibold">What:</span> {item.what}</div>}
                                  {item.action_taken && <div><span className="font-semibold">Action Taken:</span> {item.action_taken}</div>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Facility Overview Section */}
                  {tactical.facility_sites && tactical.facility_sites.length > 0 ? (() => {
                    // Compute once per tactical to avoid repeated .filter() passes during render
                    const nonTahSites = tactical.facility_sites.filter(s => !s.is_tah);
                    const tahSites = tactical.facility_sites.filter(s => s.is_tah);
                    const showSiteNames = nonTahSites.length > 1;
                    return (
                    <div>
                      <h4 className="font-semibold text-sm text-muted-foreground mb-2">
                        Facility Overview
                      </h4>
                      {nonTahSites.map((site, sIdx) => (
                        <div key={site.id || `site-${sIdx}`} className="mb-4">
                          {showSiteNames && (
                            <p className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">
                              {site.name}
                            </p>
                          )}
                          <div className="space-y-2">
                            {(site.items || []).map((item, index) => (
                              <Card key={item.id || `${site.id || sIdx}-item-${index}`} className="border-slate-100">
                                <CardContent className="py-3 px-4">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-sm">{item.name}</span>
                                    {item.added_by && (
                                      <Badge variant="outline" className="text-xs">
                                        Added by {item.added_by}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {item.value || 'No details'}
                                  </p>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      ))}
                      {/* TAH Section */}
                      {tahSites.map((site, sIdx) => (
                        <div key={site.id || `tah-${sIdx}`} className="mt-4">
                          <p className="text-xs font-semibold text-emerald-700 mb-2 uppercase tracking-wide">
                            {site.name} (TAH)
                          </p>
                          <div className="space-y-2">
                            {(site.items || []).map((item, index) => (
                              <Card key={item.id || `${site.id || sIdx}-tah-item-${index}`} className="border-emerald-100 bg-emerald-50/30">
                                <CardContent className="py-3 px-4">
                                  <span className="font-medium text-sm">{item.name}</span>
                                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                    {item.value || 'No details'}
                                  </p>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    );
                  })() : tactical.facility_overview && tactical.facility_overview.length > 0 ? (
                    <div>
                      <h4 className="font-semibold text-sm text-muted-foreground mb-2">
                        Facility Overview
                      </h4>
                      <div className="space-y-2">
                        {tactical.facility_overview.map((item, index) => (
                          <Card key={item.id || `legacy-item-${index}`} className="border-slate-100">
                            <CardContent className="py-3 px-4">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-sm">{item.name}</span>
                                {item.added_by && (
                                  <Badge variant="outline" className="text-xs">
                                    Added by {item.added_by}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                {item.value || 'No details'}
                              </p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </main>
    </div>
  );
};

export default ArchiveView;
